// Recovery for code-split chunks that vanished mid-session.
//
// Every route in App.js is a lazy import, so its chunk is fetched the moment the
// user CLICKS it — often many minutes after index.html loaded. Amplify replaces
// the whole deployment atomically on each build: the instant a deploy lands,
// every hashed file from the previous build is gone. A tab opened before the
// deploy therefore asks for a chunk that no longer exists, gets a 404, and
// webpack throws ChunkLoadError.
//
// This is NOT a crash. It means "a newer build shipped while you were sitting
// here". The correct response is to reload — index.html is served with
// `max-age=0`, so a reload always fetches the new HTML and the new bundle, and
// the user lands where they were trying to go. Showing them the crash card
// instead leaves them stuck on a dead end that "Try again" cannot fix, because
// retrying re-requests the same dead URL.

// A reload that does not fix the problem must not reload again forever. Cooldown
// rather than a one-shot flag: a one-shot flag has to be cleared on the next
// successful chunk load, which re-arms it for the NEXT deploy — and that is the
// exact state where a genuinely missing chunk starts a reload loop. A time
// window bounds the loop no matter how the flag is cleared.
const RELOAD_AT_KEY = "frdm.chunkReloadAt";
const RELOAD_COOLDOWN_MS = 15000;

// sessionStorage throws in some privacy modes and inside sandboxed frames. If we
// cannot read the last-reload time we must still refuse to reload twice, so keep
// an in-memory copy that works even when storage is unavailable.
let lastReloadAt = 0;

const readLastReloadAt = () => {
  try {
    const stored = Number(sessionStorage.getItem(RELOAD_AT_KEY));
    return Number.isFinite(stored) ? Math.max(stored, lastReloadAt) : lastReloadAt;
  } catch (e) {
    return lastReloadAt;
  }
};

const writeLastReloadAt = (now) => {
  lastReloadAt = now;
  try {
    sessionStorage.setItem(RELOAD_AT_KEY, String(now));
  } catch (e) {
    // In-memory copy above is the fallback; nothing else to do.
  }
};

/**
 * True when an error means "the JS/CSS file I asked for is not there any more".
 * Covers webpack's ChunkLoadError, the mini-css-extract stylesheet equivalent,
 * and the native dynamic-import failure some browsers report instead.
 */
export const isChunkLoadError = (error) => {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  const message = String(error.message || error);
  return (
    /Loading chunk \S+ failed/i.test(message) ||
    /Loading CSS chunk \S+ failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
};

/**
 * Reload to pick up the newly deployed build. Returns false if we already
 * reloaded within the cooldown — in that case the chunk is genuinely missing
 * (a broken deploy, not a superseded one) and the caller should surface the
 * error instead of looping.
 */
export const reloadForNewBuild = () => {
  const now = Date.now();
  if (now - readLastReloadAt() < RELOAD_COOLDOWN_MS) return false;
  writeLastReloadAt(now);
  // `true` is ignored by modern browsers, so force freshness by going through
  // the same URL rather than trusting a soft reload to re-request index.html.
  window.location.reload();
  return true;
};
